import { useMemo, useRef } from 'react';
import useInfiniteStaticProducts from 'hooks/useInfiniteStaticProducts';
import useAdvancedSearchContext from 'components/consumer/consumer-search/advanced-search-next/hooks/useAdvancedSearchContext';
import { useDeferredValue } from '../../../hooks/reactShims';
import { getVariantsByMatchedColorFirst, buildProductsInAlgoliaOrder } from '../utils';

/**
 * Derives product IDs from Algolia hits, fetches full product data, maps
 * variant highlighting, orders results to match Algolia ranking, and keeps
 * previously-visible cards only while they still belong to the current hit
 * set (e.g. GraphQL enrichment in flight). After a filter/query change the
 * hit IDs differ — stale cards are dropped so the grid shows a skeleton
 * instead of the previous unfiltered products. If enrichment returns nothing
 * for the current hits, the grid stays empty (no stale fallback).
 */
const useDisplayProducts = ({
  items,
  searchBoxQuery,
  results,
  seed
}) => {
  const previousProductsRef = useRef([]);
  const {
    selectedColors,
    currentProductIndex
  } = useAdvancedSearchContext();
  const selectedColorLabels = useMemo(() => Object.entries(selectedColors || {}).filter(([, isSelected]) => isSelected).map(([color]) => color), [selectedColors]);

  // Prefer SearchBox query – results.query can be stale/empty when filters
  // change (e.g. ready to ship)
  const searchKey = useMemo(() => {
    const query = searchBoxQuery || results?.query || '';
    const index = currentProductIndex || results?.index || '';
    const params = results?.params || '';
    const paramsWithoutPage = params.split('&').filter(param => !param.startsWith('page=')).join('&');
    return `${index}-${query}-${paramsWithoutPage}`;
  }, [currentProductIndex, searchBoxQuery, results?.query, results?.index, results?.params]);
  const productIdsRaw = useMemo(() => {
    if (!items?.length) return [];
    return items.map(hit => {
      const id = hit.product_id;
      return typeof id === 'string' ? parseInt(id, 10) : id;
    }).filter(id_0 => id_0 > 0 && !Number.isNaN(id_0));
  }, [items]);
  const currentHitIds = useMemo(() => new Set(productIdsRaw.map(id_1 => String(id_1))), [productIdsRaw]);

  // Defer product IDs so we don't trigger immediate re-fetches while typing
  const productIds = useDeferredValue(productIdsRaw);
  const {
    products: productsWithVariantsToShow,
    loading
  } = useInfiniteStaticProducts({
    productIds,
    searchKey,
    seed
  }, {
    skipSorting: true
  });
  const productsWithHighlightedVariantsFirst = useMemo(() => productsWithVariantsToShow.map(product => ({
    ...product,
    variantsToShow: getVariantsByMatchedColorFirst(product, items, selectedColorLabels)
  })), [productsWithVariantsToShow, items, selectedColorLabels]);
  const productsInAlgoliaOrder = useMemo(() => buildProductsInAlgoliaOrder(productsWithHighlightedVariantsFirst, items), [items, productsWithHighlightedVariantsFirst]);

  // Keep previous products visible until enrichment is ready only when they
  // still match the current Algolia hits (pagination / same-result enrichment).
  // When Algolia returns 0 hits, or hits change to a different ID set (filter
  // refine), clear so the parent can show a skeleton / empty instead of stale
  // cards. When enrichment finishes empty for these hits, stay empty.
  const displayProducts = useMemo(() => {
    if (!items?.length) {
      previousProductsRef.current = [];
      return [];
    }
    if (productsInAlgoliaOrder?.length > 0) {
      previousProductsRef.current = productsInAlgoliaOrder;
      return productsInAlgoliaOrder;
    }
    const previous = previousProductsRef.current;
    const previousIds = new Set((previous || []).map(product_0 => String(product_0?.id ?? '')));
    // Subset, not equality: Load more (and widening a filter) grows
    // currentHitIds while previous remains a valid subset. Requiring equal
    // size would flash a skeleton on pagination. Clear only when a previous
    // id is gone (narrowed / different filter).
    const previousStillMatchesCurrentHits = previousIds.size > 0 && [...previousIds].every(id_2 => currentHitIds.has(id_2));
    if (previousStillMatchesCurrentHits) {
      return previous;
    }
    previousProductsRef.current = [];
    return [];
  }, [items, currentHitIds, productsInAlgoliaOrder]);
  return {
    displayProducts,
    loading,
    productIds,
    productIdsRaw
  };
};
export default useDisplayProducts;